<template>
  <EasyModal
    v-model="isOpened"
    :title="props.title"
    :modal-class="`easy-dialog ${props.modalClass}`"
    :content-class="props.contentClass"
    :error-description="props.errorDescription"
    :width="props.width"
    :loading="props.loading"
    :hide-footer="props.hideFooter"
    :hide-header="props.hideHeader"
    :min-width="props.minWidth"
    :absolute="props.absolute"
    :show-overlay="props.showOverlay"
    :z-index="props.zIndex"
    hide-sidebar
    data-cy="easy-dialog"
    @close="close"
    @open="open"
  >
    <div
      v-if="!loading"
      class="easy-dialog--body"
      data-cy="body__easy-dialog"
    >
      <slot />
    </div>

    <template #footer>
      <div
        class="easy-dialog--footer"
        data-cy="footer__easy-dialog"
      >
        <slot name="footer" />
      </div>
    </template>
  </EasyModal>
</template>

<script setup lang="ts">
import EasyModal from "@/src/easy-ui/components/EasyModal.vue";
import { computed, onMounted, watch, WritableComputedRef, ref, onUnmounted } from "vue";
import easyDialogStore from "@/src/easy-ui/store/easyDialogStore";

interface Props {
  modelValue: boolean;
  loading?: boolean;
  contentClass?: string | Array<string>;
  modalClass?: string;
  errorDescription?: string;
  width?: number | string;
  minWidth?: number | string;
  title?: string;
  hideFooter?: boolean;
  hideHeader?: boolean;
  absolute?: boolean;
  showOverlay?: boolean;
  zIndex?: string | number;
  hideBodyScrollbar?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  contentClass: "",
  modalClass: "",
  errorDescription: "",
  width: "",
  title: "",
  minWidth: "",
  hideFooter: false,
  hideHeader: false,
  absolute: false,
  showOverlay: true,
  zIndex: "unset",
  hideBodyScrollbar: true,
});

interface Emits {
  (e: "update:modelValue", isOpened: boolean): void;
  (e: "close"): void;
  (e: "open"): void;
}
const emit = defineEmits<Emits>();

const isOpened: WritableComputedRef<boolean> = computed({
  get: () => props.modelValue,
  set: (isOpened: boolean) => emit("update:modelValue", isOpened),
});

const easyDialogUuid = ref<string>("");

watch(
  () => props.modelValue,
  isOpened => {
    easyDialogStore.updateDialogOpened(easyDialogUuid.value, isOpened);
  }
);

onMounted(() => {
  easyDialogUuid.value = easyDialogStore.registerNewDialog({
    isOpened: props.modelValue,
    hideBodyScrollbar: props.hideBodyScrollbar,
  });
});

const close = () => {
  easyDialogStore.updateDialogOpened(easyDialogUuid.value, false);
  emit("close");
};

const open = () => {
  easyDialogStore.updateDialogOpened(easyDialogUuid.value, true);
  emit("open");
};

onUnmounted(() => {
  easyDialogStore.updateDialogOpened(easyDialogUuid.value, false);
});
</script>
